import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Send, RotateCcw, Sparkles } from 'lucide-react';
import ChatMessage from './ChatMessage';
import QuickReplies from './QuickReplies';

const MAX_INPUT = 300;

const TypingIndicator = () => (
  <div className="flex items-start gap-2">
    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-teal-600 text-white">
      <Sparkles className="h-3.5 w-3.5" />
    </div>
    <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-gray-100 bg-white px-3.5 py-3 shadow-sm">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
          style={{ animationDelay: `${i * 140}ms` }}
        />
      ))}
    </div>
  </div>
);

const ChatPanel = ({
  audience,
  messages,
  isTyping,
  streamingId,
  onClose,
  onSend,
  onChip,
  onAction,
  onReset,
  onStreamDone,
}) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const endRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const busy = isTyping || Boolean(streamingId);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    onSend(text);
  };

  const lastMessage = messages[messages.length - 1];
  const showReplies =
    lastMessage && lastMessage.role === 'assistant' && !isTyping && streamingId !== lastMessage.id;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="ChooseRightCareer chat assistant"
      className="fixed inset-0 z-[70] flex flex-col bg-white sm:inset-auto sm:bottom-[10rem] sm:right-6 sm:h-[min(560px,calc(100vh-12rem))] sm:w-[380px] sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-2xl"
    >
      <header className="flex items-center gap-3 rounded-t-none border-b border-gray-100 bg-gradient-to-r from-blue-600 to-teal-600 px-4 py-3.5 text-white sm:rounded-t-2xl">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
          <Sparkles className="h-4.5 w-4.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">Career Assistant</p>
          <p className="truncate text-[11.5px] text-white/80">
            {audience === 'school' ? 'School partnerships · Instant answers' : 'Instant answers · No waiting'}
          </p>
        </div>
        <button
          type="button"
          onClick={onReset}
          title="Start over"
          aria-label="Start a new conversation"
          className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div
        ref={scrollRef}
        aria-live="polite"
        aria-atomic="false"
        className="flex-1 space-y-3 overflow-y-auto bg-gray-50/70 px-3.5 py-4"
      >
        {messages.map((m) => (
          <ChatMessage
            key={m.id}
            message={m}
            streaming={streamingId === m.id}
            onStreamTick={scrollToBottom}
            onStreamDone={() => onStreamDone(m.id)}
          />
        ))}

        {isTyping && <TypingIndicator />}

        {showReplies && (
          <QuickReplies
            chips={lastMessage.chips}
            actions={lastMessage.actions}
            onChip={onChip}
            onAction={onAction}
            disabled={busy}
          />
        )}

        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="border-t border-gray-100 bg-white px-3 py-2.5 sm:rounded-b-2xl">
        <div className="flex items-end gap-2">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_INPUT))}
            placeholder="Ask about fees, timing, classes…"
            aria-label="Type your question"
            maxLength={MAX_INPUT}
            className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[14px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-200"
          />
          <button
            type="submit"
            disabled={!draft.trim() || busy}
            aria-label="Send message"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-teal-600 text-white transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="px-1 pt-1.5 text-[10.5px] leading-snug text-gray-400">
          Instant answers from a fixed help guide, not a person. For anything else the team replies on WhatsApp.
        </p>
      </form>
    </div>
  );
};

export default ChatPanel;
